import { httpsCallable } from 'firebase/functions';
import { functions, db, auth } from './config';
import { getFunctions } from "firebase/functions";
import { getFirestore, collection, getDocs } from "firebase/firestore";

// Export the functions instance
export { functions };

// Process a document with AI
export const processDocumentWithAI = async (fileId, jobType, enhancementType = null) => {
  try {
    const processDocument = httpsCallable(functions, 'processDocumentWithAI');
    const result = await processDocument({ fileId, jobType, enhancementType });
    return result.data;
  } catch (error) {
    throw new Error(`Error processing document: ${error.message}`);
  }
};

// Process a spreadsheet with AI
export const processSpreadsheetWithAI = async (fileId, jobType) => {
  try {
    const processSpreadsheet = httpsCallable(functions, 'processSpreadsheetWithAI');
    const result = await processSpreadsheet({ fileId, jobType });
    return result.data;
  } catch (error) {
    throw new Error(`Error processing spreadsheet: ${error.message}`);
  }
};

// Create Stripe checkout session
export const createStripeCheckoutSession = async (amount, currency, userId, credits, packageId = null, ip = null, email = null) => {
  try {
    console.log(`Creating Stripe checkout session for ${credits} credits at ${amount} ${currency}`);

    // Validate inputs
    if (!userId) {
      throw new Error('User ID is required');
    }

    // Check if user is authenticated in Firebase Auth
    const { auth } = await import('./config');
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated in Firebase Auth');
    }

    console.log('Firebase Auth current user:', currentUser.uid);
    console.log('Provided userId:', userId);

    // Force refresh the Firebase Auth token to ensure it's valid
    try {
      await currentUser.getIdToken(true);
      console.log('Firebase Auth token refreshed successfully');
    } catch (tokenError) {
      console.error('Error refreshing Firebase Auth token:', tokenError);
      throw new Error('Failed to refresh authentication token');
    }

    // Get IP address for country detection
    let userIp = ip;
    if (!userIp) {
      try {
        // Try multiple IP detection services with fallback
        const services = [
          'https://api.ipify.org?format=json',
          'https://ipapi.co/json/',
          'https://ipinfo.io/json'
        ];

        for (const service of services) {
          try {
            console.log(`Trying IP detection service: ${service}`);
            const ipResponse = await fetch(service, {
              timeout: 3000,
              mode: 'cors'
            });

            if (ipResponse.ok) {
              const ipData = await ipResponse.json();
              console.log(`IP detection successful with ${service}:`, ipData);

              // Handle different response formats
              if (ipData.ip) {
                userIp = ipData.ip;
                break;
              } else if (ipData.query) {
                userIp = ipData.query;
                break;
              }
            }
          } catch (serviceError) {
            console.log(`Service ${service} failed:`, serviceError.message);
            continue;
          }
        }

        if (!userIp) {
          console.log('All IP detection services failed, using fallback');
          // Use default fallback for all environments
          userIp = '127.0.0.1';
        }
      } catch (ipError) {
        console.error("Error detecting IP:", ipError);
      }
    }

    // Get domain for success/cancel URLs
    const domain = window.location.origin;

    // Try Firebase callable function first
    try {
      // Call Firebase function
      console.log('Calling Firebase Cloud Function with data:', {
        amount,
        currency,
        userId,
        credits,
        packageId,
        ip: userIp,
        email
      });

      const createSession = httpsCallable(functions, 'createStripeCheckoutSession');
      const result = await createSession({
        amount,
        currency,
        userId,
        credits,
        packageId,
        ip: userIp,
        email,
        successUrl: `${domain}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${domain}/payment-cancel?session_id={CHECKOUT_SESSION_ID}`
      });

      // Check for redirect to Razorpay for Indian users
      if (result.data.redirect === 'razorpay') {
        console.log("Redirecting to Razorpay as recommended by the backend");
        return await createRazorpayOrder(amount, 'INR', userId, credits, packageId);
      }

      console.log("Successfully created Stripe checkout session", result.data);
      return result.data;
    } catch (callableError) {
      console.log("Cloud Function authentication failed, using API endpoint fallback:", callableError.message);

      // If callable function fails, try direct API endpoint
      const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/createStripeCheckoutApi', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          currency,
          userId,
          credits,
          packageId,
          ip: userIp,
          email,
          successUrl: `${domain}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${domain}/payment-cancel?session_id={CHECKOUT_SESSION_ID}`
        })
      });

      if (!response.ok) {
        throw new Error(`API endpoint failed with status ${response.status}`);
      }

      const data = await response.json();

      // Check for redirect to Razorpay for Indian users
      if (data.redirect === 'razorpay') {
        console.log("Redirecting to Razorpay as recommended by the API");
        return await createRazorpayOrder(amount, 'INR', userId, credits, packageId);
      }

      console.log("Successfully created Stripe checkout session via API fallback", data);
      return data;
    }
  } catch (error) {
    console.error(`Error creating Stripe checkout session: ${error.message}`);
    throw new Error(`Failed to create checkout session: ${error.message}`);
  }
};

// Create a Razorpay order
export const createRazorpayOrder = async (amount, currency, userId, credits, packageId = null) => {
  try {
    console.log(`Creating Razorpay order for ${credits} credits at ${amount} ${currency}`);

    // Get IP address for country detection
    let ip = '';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ip = ipData.ip;
    } catch (ipError) {
      console.error("Error detecting IP:", ipError);
    }

    // Try Firebase callable function first
    try {
      // Call Firebase function
      const createOrder = httpsCallable(functions, 'createRazorpayOrder');
      const result = await createOrder({
        amount,
        currency,
        userId,
        credits,
        packageId,
        ip
      });

      // Check for redirect to Stripe for non-Indian users
      if (result.data.redirect === 'stripe') {
        console.log("Redirecting to Stripe as recommended by the backend");
        return await createStripeCheckoutSession(amount, 'USD', userId, credits, packageId);
      }

      console.log("Successfully created Razorpay order", result.data);
      return result.data;
    } catch (callableError) {
      console.error("Callable function failed, trying API endpoint:", callableError);

      // For authentication errors, try the API endpoint which doesn't require Firebase auth
      if (callableError.code === 'unauthenticated') {
        console.log("Authentication failed, using API endpoint as fallback");
      }

      // If callable function fails, try direct API endpoint
      const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/createRazorpayOrderApi', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount,
          currency,
          userId,
          credits,
          packageId
        })
      });

      if (!response.ok) {
        throw new Error(`API endpoint failed with status ${response.status}`);
      }

      const data = await response.json();

      // Check for redirect to Stripe for non-Indian users
      if (data.redirect === 'stripe') {
        console.log("Redirecting to Stripe as recommended by the API");
        return await createStripeCheckoutSession(amount, 'USD', userId, credits, packageId);
      }

      console.log("Successfully created Razorpay order via API", data);
      return data;
    }
  } catch (error) {
    console.error(`Error creating Razorpay order: ${error.message}`);
    throw new Error(`Error creating Razorpay order: ${error.message}`);
  }
};

// Verify a Razorpay payment
export const verifyRazorpayPayment = async (paymentId, orderId, signature, userId) => {
  try {
    console.log(`Verifying Razorpay payment: ${paymentId}, order: ${orderId}`);

    // Try Firebase callable function first
    try {
      const verifyPayment = httpsCallable(functions, 'verifyRazorpayPayment');
      const result = await verifyPayment({
        paymentId,
        orderId,
        signature,
        userId
      });

      console.log("Payment verification result:", result.data);
      return result.data;
    } catch (callableError) {
      console.error("Callable function failed, trying API endpoint:", callableError);

      // If callable function fails, try direct API endpoint
      const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/verifyRazorpayPaymentApi', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          paymentId,
          orderId,
          signature,
          userId
        })
      });

      if (!response.ok) {
        throw new Error(`API endpoint failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("Payment verification result from API:", data);
      return data;
    }
  } catch (error) {
    console.error(`Error verifying payment: ${error.message}`);
    throw new Error(`Error verifying payment: ${error.message}`);
  }
};

// Check status of a Stripe payment
export const checkStripePaymentStatus = async (sessionId) => {
  try {
    console.log(`Checking Stripe payment status for session: ${sessionId}`);

    const checkStatus = httpsCallable(functions, 'checkStripePaymentStatus');
    const result = await checkStatus({ sessionId });

    console.log("Payment status check result:", result.data);
    return result.data;
  } catch (error) {
    console.error(`Error checking payment status: ${error.message}`);
    throw new Error(`Error checking payment status: ${error.message}`);
  }
};

// Get credit packages
export const getCreditPackages = async () => {
  try {
    console.log("Getting credit packages from Firebase function");

    // Get IP address for country detection
    let ip = '';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ip = ipData.ip;
    } catch (ipError) {
      console.error("Error detecting IP:", ipError);
    }

    // Try Firebase callable function first
    try {
      // Call Firebase function
      const getPackages = httpsCallable(functions, 'getCreditPackages');
      const result = await getPackages({ ip });
      console.log("Successfully retrieved packages from Firebase function", result.data);
      return result.data;
    } catch (callableError) {
      console.error("Callable function failed, trying API endpoint:", callableError);

      // If callable function fails, try direct API endpoint
      const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/getCreditPackagesApi');

      if (!response.ok) {
        throw new Error(`API endpoint failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("Successfully retrieved packages from API endpoint", data);
      return data;
    }
  } catch (apiError) {
    console.error(`Error getting credit packages from API: ${apiError.message}`);

    try {
      console.log("Trying to get packages from Firestore");
      // Try to get packages from Firestore as fallback
      const packagesRef = collection(db, "creditPackages");
      const querySnapshot = await getDocs(packagesRef);

      if (!querySnapshot.empty) {
        const packages = [];
        querySnapshot.forEach(doc => {
          packages.push({
            package_id: doc.id,
            ...doc.data()
          });
        });

        console.log("Successfully retrieved packages from Firestore", packages);
        return {
          packages,
          currency: 'USD',
          symbol: '$',
          country: 'US',
          payment_provider: 'stripe',
          job_types: getJobTypes()
        };
      }
    } catch (firestoreError) {
      console.error(`Error getting credit packages from Firestore: ${firestoreError.message}`);
    }

    // Use hardcoded fallback packages as last resort
    console.log("Using hardcoded fallback packages");
    const fallbackPackages = [
      {
        package_id: "starter",
        credits: 50,
        price: 2.99,
        currency: "USD",
        symbol: "$",
        description: "Starter Pack",
        recommended: false
      },
      {
        package_id: "small",
        credits: 100,
        price: 5.99,
        currency: "USD",
        symbol: "$",
        description: "Small Pack",
        recommended: false
      },
      {
        package_id: "medium",
        credits: 250,
        price: 11.99,
        currency: "USD",
        symbol: "$",
        description: "Popular Pack",
        recommended: true
      },
      {
        package_id: "large",
        credits: 500,
        price: 21.99,
        currency: "USD",
        symbol: "$",
        description: "Pro Pack",
        recommended: false
      },
      {
        package_id: "enterprise",
        credits: 1000,
        price: 39.99,
        currency: "USD",
        symbol: "$",
        description: "Enterprise Pack",
        recommended: false
      },
      {
        package_id: "mega",
        credits: 2500,
        price: 89.99,
        currency: "USD",
        symbol: "$",
        description: "Mega Pack",
        recommended: false
      }
    ];

    return {
      packages: fallbackPackages,
      currency: 'USD',
      symbol: '$',
      country: 'US',
      payment_provider: 'stripe',
      job_types: getJobTypes()
    };
  }
};

// Get custom credit price
export const getCustomCreditPrice = async (credits) => {
  try {
    console.log(`Getting price for ${credits} custom credits`);

    // Get IP address for country detection
    let ip = '';
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      const ipData = await ipResponse.json();
      ip = ipData.ip;
    } catch (ipError) {
      console.error("Error detecting IP:", ipError);
    }

    const getPrice = httpsCallable(functions, 'getCustomCreditPrice');
    const result = await getPrice({ credits, ip });
    console.log("Custom credit price result:", result.data);
    return result.data;
  } catch (error) {
    console.error(`Error getting custom credit price: ${error.message}`);
    throw new Error(`Error getting custom credit price: ${error.message}`);
  }
};

// Get job types with costs (based on server.py implementation)
export const getJobTypes = () => {
  return {
    // Excel processing options
    "excel_formatting": { "cost": 5, "description": "Plain formatting and structure" },
    "excel_analysis": { "cost": 10, "description": "Data analysis with bullet-point insights" },
    "excel_dashboard": { "cost": 15, "description": "Auto-generated charts and KPIs" },

    // Document enhancement types with varying costs
    "document_business": { "cost": 8, "description": "Business letter enhancement" },
    "document_blog": { "cost": 10, "description": "Blog post optimization" },
    "document_academic": { "cost": 12, "description": "Academic paper improvement" },
    "document_general": { "cost": 8, "description": "General proofreading" },

    // Legacy support
    "excel_summary": { "cost": 10, "description": "Excel summary (legacy)" },
    "document_enhancement": { "cost": 10, "description": "Document enhancement (legacy)" }
  };
};

// Process guest payment
export const processGuestPayment = async (email, phone, fileId, jobType, enhancementType = null) => {
  try {
    const processPayment = httpsCallable(functions, 'processGuestPayment');
    const result = await processPayment({
      email,
      phone,
      fileId,
      jobType,
      enhancementType
    });
    return result.data;
  } catch (error) {
    throw new Error(`Error processing guest payment: ${error.message}`);
  }
};

// Send contact form
export const sendContactForm = async (name, email, message) => {
  try {
    const sendContact = httpsCallable(functions, 'sendContactForm');
    const result = await sendContact({ name, email, message });
    return result.data;
  } catch (error) {
    throw new Error(`Error sending contact form: ${error.message}`);
  }
};

// Cancel a payment
export const cancelPayment = async (sessionId, userId) => {
  try {
    console.log(`Canceling payment for session: ${sessionId}, user: ${userId}`);

    // Validate inputs
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    if (!userId) {
      throw new Error('User ID is required');
    }

    // Try Firebase callable function first
    try {
      const cancelPaymentFunction = httpsCallable(functions, 'cancelPayment');
      const result = await cancelPaymentFunction({
        sessionId,
        userId
      });

      console.log("Payment cancellation result:", result.data);
      return result.data;
    } catch (callableError) {
      console.error("Callable function failed, trying API endpoint:", callableError);

      // If callable function fails, try direct API endpoint
      const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/cancelPaymentApi', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API endpoint failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("Payment cancellation result from API:", data);
      return data;
    }
  } catch (error) {
    console.error(`Error canceling payment: ${error.message}`);
    throw new Error(`Error canceling payment: ${error.message}`);
  }
};

// Claim signup bonus after email verification
export const claimSignupBonus = async () => {
  try {
    // Wait for auth state to be fully established
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to claim the signup bonus');
    }

    console.log('Claiming signup bonus for user:', user.uid, 'email verified:', user.emailVerified);
    console.log('User auth state:', {
      uid: user.uid,
      email: user.email,
      emailVerified: user.emailVerified,
      isAnonymous: user.isAnonymous,
      providerData: user.providerData.map(p => p.providerId)
    });

    // Ensure user is email verified
    if (!user.emailVerified) {
      throw new Error('Please verify your email address before claiming the signup bonus');
    }

    // Force a complete auth state refresh
    await user.reload();
    console.log('User reloaded, email verified:', user.emailVerified);

    // Refresh token to ensure callable gets auth context
    const idToken = await user.getIdToken(true);
    console.log('Got fresh ID token for claimSignupBonus, length:', idToken.length);
    console.log('Token preview:', idToken.substring(0, 20) + '...');

    // Since callable function authentication is not working properly, use HTTP fallback directly
    console.log('Using HTTP fallback for claimSignupBonus...');

    const response = await fetch('https://us-central1-filezenai.cloudfunctions.net/claimSignupBonusApi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ idToken })
    });

    console.log('HTTP response status:', response.status);

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      console.error('HTTP error response:', err);
      throw new Error(err.error || `HTTP claim failed with status ${response.status}`);
    }

    const result = await response.json();
    console.log('HTTP result:', result);
    return result;
  } catch (error) {
    console.error('claimSignupBonus error message:', error.message);
    console.error('claimSignupBonus error code:', error.code);
    console.error('claimSignupBonus full error:', error);
    throw new Error(`Error claiming signup bonus: ${error.message}`);
  }
};

// Test authentication
export const testAuth = async () => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to test authentication');
    }

    console.log('Testing auth for user:', user.uid, 'email verified:', user.emailVerified);

    // Refresh token to ensure callable gets auth context
    const idToken = await user.getIdToken(true);
    console.log('Got fresh ID token for testAuth, length:', idToken.length);

    // Test the testAuth callable function
    const testFn = httpsCallable(functions, 'testAuth');
    const result = await testFn({ idToken });
    console.log('Auth test result:', result.data);
    return result.data;
  } catch (error) {
    console.error('Auth test error:', error);
    throw new Error(`Error testing auth: ${error.message}`);
  }
};

// Test connection without authentication
export const testConnection = async () => {
  try {
    console.log('Testing Firebase Functions connection...');

    const testFn = httpsCallable(functions, 'testConnection');
    const result = await testFn({ test: 'data' });
    console.log('Connection test result:', result.data);
    return result.data;
  } catch (error) {
    console.error('Connection test error:', error);
    throw new Error(`Error testing connection: ${error.message}`);
  }
};

// Email the processed file to the user
export const sendProcessedFileEmail = async (jobId, email = null) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('You must be logged in');
    await user.getIdToken(true);

    const fn = httpsCallable(functions, 'sendProcessedFileEmail');
    const result = await fn({ jobId, email });
    return result.data;
  } catch (error) {
    throw new Error(`Error sending email: ${error.message}`);
  }
}; 